import React, { Suspense, useEffect } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { BrowserRouter, useSearchParams } from "react-router-dom";
import "./assests/styles/index.css";
import "@shopflo/ui/styles.css";
import { AuthProvider } from "lib/contexts/AuthProvider";
import { UserProvider } from "lib/contexts/UserProvider";
import { CheckoutProvider } from "lib/contexts/CheckoutProvider";
import { MerchantProvider } from "lib/contexts/MerchantProvider";
import { addPolyfills } from "lib/core/polyfills";
import Skeleton from "components/common/loaders/Skeleton";
import {
  classNames,
  inIframe,
  isMobile,
  isThirdPartyCookieEnabled,
  publishPostMessage,
} from "lib/utils/helpers";
import { PaymentProvider } from "lib/contexts/PaymentProvider";
import { LoaderProvider } from "lib/contexts/LoaderContext";
import useSentry from "lib/hooks/useSentry";
import * as Sentry from "@sentry/react";
import { WishlistProvider } from "lib/contexts/WishlistProvider";
import { MerchantUIConfigProvider } from "lib/contexts/MerchantUIConfigProvider";
import { eventTypes } from "lib/utils/constants";
import { ErrorType } from "lib/types/error";

addPolyfills();
// initialiseNRM();

const searchParams = new URLSearchParams(window.location.search);
const page = searchParams.get("page")?.toLowerCase();
const isMarketingPopup = searchParams.get("page") === "marketing-popup";

const container = document.getElementById("root");
const root = createRoot(container!); // createRoot(container!) if you use TypeScript

const redirectToParent =
  inIframe() &&
  !isThirdPartyCookieEnabled() &&
  ![
    "cart",
    "empty-cart",
    "login-status-check",
    "ghost-modal",
    "marketing-popup",
    "experiments-widget",
  ].includes(page ?? "");

if (redirectToParent) {
  root.render(
    <React.StrictMode>
      <BrowserRouter basename={import.meta.env.VITE_PUBLIC_URL}>
        <RedirectWrapper />
      </BrowserRouter>
    </React.StrictMode>,
  );
} else {
  root.render(
    <React.StrictMode>
      <BrowserRouter basename={import.meta.env.VITE_PUBLIC_URL}>
        <AppShell>
          <Suspense fallback={isMarketingPopup ? <></> : <Skeleton />}>
            <LoaderProvider>
              <AuthProvider>
                <UserProvider>
                  <MerchantProvider>
                    <MerchantUIConfigProvider>
                      <WishlistProvider>
                        <CheckoutProvider>
                          <PaymentProvider>
                            <App />
                          </PaymentProvider>
                        </CheckoutProvider>
                      </WishlistProvider>
                    </MerchantUIConfigProvider>
                  </MerchantProvider>
                </UserProvider>
              </AuthProvider>
            </LoaderProvider>
          </Suspense>
        </AppShell>
      </BrowserRouter>
    </React.StrictMode>,
  );
}

function RedirectWrapper() {
  useSentry();

  useEffect(() => {
    try {
      const redirectUrl = new URL(document.location.href);
      publishPostMessage(eventTypes.PARENT_REDIRECT, {
        redirectUrl: redirectUrl.href,
        clearCart: false,
        isSso: page === "login-sso",
      });
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("error_type", ErrorType.PARENT_REDIRECT_ERROR);
        if (err instanceof Error) {
          scope.setExtra("errMessage", err?.message);
          scope.setExtra("errUrl", document.location.href);
        }
        scope.captureException(err);
      });
    }
  }, []);

  return <Skeleton />;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const pageName = searchParams.get("page");
  const isCartPage = ["cart", "empty-cart"].includes(pageName?.toLowerCase() ?? "");
  const isSsoLoginPage = pageName === "login-sso";
  const isMarketingPopup = pageName === "marketing-popup";
  const isWishlistPage = pageName === "wishlist";
  const isMockCheckoutPage = pageName === "mock-checkout";
  const isExperimentWidgetPage = pageName === "experiments-widget";

  useSentry();

  if (isMarketingPopup) {
    return (
      <div
        className={classNames(
          "overflow-hidden md:h-fit md:w-[25rem] w-full",
          "md:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]",
        )}>
        {children}
      </div>
    );
  }

  if (isCartPage) {
    return (
      <div className="h-screen w-full overflow-hidden bg-gray-lightest md:h-[85vh] md:w-[25rem] md:rounded-l-[28px] md:rounded-r-none  md:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]">
        {children}
      </div>
    );
  }

  if (isSsoLoginPage) {
    return (
      <div
        className={`flex h-screen w-full overflow-hidden bg-gray-lighter ${
          inIframe() ? `justify-center` : `flex-col items-center justify-center`
        }`}>
        <div
          className={classNames(
            `h-[400px] w-[360px] border border-carbon-lighter bg-white md:h-[400px]`,
            isMobile() ? "rounded-xl" : "relative w-[360px] rounded-[20px] md:w-[360px]",
            inIframe() ? `!w-full !rounded-xl` : ``,
          )}>
          {children}
        </div>
      </div>
    );
  }

  if (isWishlistPage) {
    return <div className="h-full w-full overflow-hidden bg-transparent">{children}</div>;
  }

  if (isMockCheckoutPage) {
    return (
      <div
        className={classNames(
          "h-screen w-full overflow-hidden bg-gray-lightest md:h-[85vh] md:w-[69rem]",
          isMobile() ? "" : inIframe() ? `rounded-[28px]` : "md:rounded-[28px]",
          "md:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]",
        )}>
        {children}
      </div>
    );
  }

  if (isExperimentWidgetPage) {
    return <div className="h-full w-full overflow-hidden bg-transparent">{children}</div>;
  }

  return (
    <div
      className={classNames(
        "h-screen w-full overflow-hidden bg-gray-lightest md:h-[85vh] md:w-[25rem]",
        isMobile() ? "" : inIframe() ? `rounded-[28px]` : "md:rounded-[28px]",
        "md:shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]",
      )}>
      {children}
    </div>
  );
}

window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});
